import { Percentage } from '@ui/components/Amount/Percentage';
import { Copy, ShareLink, X as XIcon } from '@ui/components/icons';
import {
  ShareIdentityControls,
  ShareIdentityProfile,
  useShareIdentity,
} from '@ui/components/share/useShareIdentity';
import { basicToaster } from '@ui/components/Toast';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { Textarea } from '@ui/lib/park/ui/textarea';
import {
  ClosePositionDetailExecutionTypeEnum,
  TradeHistoryResponseOperationTypeEnum,
} from '@ui/services/backend/generated';
import {
  ClosePosition,
  ExecuteCloseSuccessful,
  ExecuteDecreaseOrderSuccessful,
  PositionDecreased,
  TradeHistory,
  UserCloseTradeSuccessful,
} from '@ui/services/backend/hooks/useTradeHistory/type';
import {
  canShareNodeImage,
  copyNodeImageToClipboard,
  downloadNodeAsPng,
  shareNodeImage,
} from '@ui/utils/image/exportNodeAsImage';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, HStack, Stack, styled, VStack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { useTradePairInfosByPairBase } from '../../../../hooks/trade/useTradePairInfos';
import { useGetExecutionFeeUsd } from '../../hooks/useGetExecutionFee';
import {
  ShareInviteCodeToggle,
  useShareReferralCode,
} from '../ShareInviteCodeToggle';

const PEPE_IMAGES = [
  '/images/share-pepe/ultra-pepe-1.svg',
  '/images/share-pepe/ultra-pepe-2.svg',
  '/images/share-pepe/ultra-pepe-3.svg',
  '/images/share-pepe/ultra-pepe-4.svg',
];

// The share card is authored at a fixed size and scaled to fit the modal
// width, so proportions (and the exported image) are identical on every device.
const SHARE_CARD_WIDTH = 500;
const SHARE_CARD_HEIGHT = 260;

export const HistoryShareOverlay = ({
  history,
  children,
}: {
  history: TradeHistory;
  children?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [shareText, setShareText] = useState(
    'Trade $MON perps seamlessly on @LeverUp_xyz',
  );
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(true);
  // Mobile/in-app browsers can't reliably copy images to the clipboard, but
  // they can open the native share sheet (Save to Photos + share anywhere).
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(canShareNodeImage());
  }, []);
  // Disable the image button while rendering so a half-loaded card can't be
  // saved/copied (image generation waits for all images to finish loading).
  const [imageBusy, setImageBusy] = useState(false);
  const imageBusyRef = useRef(false);

  const shareRef = useRef<HTMLDivElement | null>(null);
  const scalerWrapRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const [cardScale, setCardScale] = useState(1);
  const identity = useShareIdentity();

  // Scale the fixed-size share card down to fit the modal width.
  const measureCardScale = useCallback(() => {
    const el = scalerWrapRef.current;
    if (!el) return;
    const width = el.clientWidth || el.getBoundingClientRect().width;
    if (width > 0) setCardScale(Math.min(1, width / SHARE_CARD_WIDTH));
  }, []);

  // Callback ref so the ResizeObserver attaches the moment the node mounts.
  // The dialog content is portaled, so it appears in a later commit than an
  // [open] effect would fire — a plain ref check bails early and the card
  // stays stuck at scale 1 (overflowing narrow screens).
  const setScalerWrapRef = useCallback(
    (node: HTMLDivElement | null) => {
      scalerWrapRef.current = node;
      roRef.current?.disconnect();
      roRef.current = null;
      if (!node) return;
      measureCardScale();
      requestAnimationFrame(measureCardScale);
      const ro = new ResizeObserver(measureCardScale);
      ro.observe(node);
      roRef.current = ro;
    },
    [measureCardScale],
  );

  useEffect(() => {
    window.addEventListener('resize', measureCardScale);

    return () => window.removeEventListener('resize', measureCardScale);
  }, [measureCardScale]);
  const { address } = useAccount();
  const { referralCode, isLoading: isReferralCodeLoading } =
    useShareReferralCode(address);
  const priceDisplayDecimals = useTokenDisplayDecimals(
    history.pair.baseSymbol,
    history.pair.priceDisplayDecimals,
  );
  const latestPairPrice = usePriceLatest(history.pair.baseSymbol);
  const latestCollateralPrice = usePayTokenPriceLatest(history.tokenIn);
  const collateralPrice = history.tokenInPrice ?? latestCollateralPrice;
  const pairPrice = useMemo(() => {
    switch (history.type) {
      case TradeHistoryResponseOperationTypeEnum.OpenLimitOrder:
        return history.detail.price;
      case TradeHistoryResponseOperationTypeEnum.OpenMarketTrade:
      case TradeHistoryResponseOperationTypeEnum.OpenPosition:
      case TradeHistoryResponseOperationTypeEnum.PositionIncreased:
        return history.detail.atPrice;
      case TradeHistoryResponseOperationTypeEnum.MarketPendingTrade:
        return history.detail.price;
      case TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful:
      case TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful:
      case TradeHistoryResponseOperationTypeEnum.ClosePosition:
      case TradeHistoryResponseOperationTypeEnum.PositionDecreased:
      case TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful:
        return history.detail.closePrice;
      default:
        return history.entryPrice ?? latestPairPrice;
    }
  }, [history, latestPairPrice]);

  const executionFeeUsd = useGetExecutionFeeUsd();
  const pairInfo = useTradePairInfosByPairBase(history.pair.address);

  const isClosedTrade = (
    h: TradeHistory,
  ): h is
    | UserCloseTradeSuccessful
    | ExecuteCloseSuccessful
    | ExecuteDecreaseOrderSuccessful
    | ClosePosition
    | PositionDecreased => {
    return (
      h.type === TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful ||
      h.type ===
        TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful ||
      h.type === TradeHistoryResponseOperationTypeEnum.ClosePosition ||
      h.type === TradeHistoryResponseOperationTypeEnum.PositionDecreased ||
      h.type === TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful
    );
  };

  const eventLabel = useMemo(() => {
    if (
      history.type ===
        TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful ||
      history.type ===
        TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful
    ) {
      const exec = history as
        | ExecuteCloseSuccessful
        | ExecuteDecreaseOrderSuccessful;
      if (
        exec.detail?.executionType === ClosePositionDetailExecutionTypeEnum.Liq
      )
        return 'Liquidation';
      if (
        exec.detail?.executionType === ClosePositionDetailExecutionTypeEnum.Tp
      )
        return 'Take Profit';
      if (
        exec.detail?.executionType === ClosePositionDetailExecutionTypeEnum.Sl
      )
        return 'Stop Loss';
      if (
        exec.detail?.executionType === ClosePositionDetailExecutionTypeEnum.Adl
      )
        return 'ADL';
    }
    if (
      history.type ===
      TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful
    )
      return 'Close';
    if (history.type === TradeHistoryResponseOperationTypeEnum.ClosePosition)
      return 'Close';
    if (
      history.type === TradeHistoryResponseOperationTypeEnum.PositionDecreased
    )
      return 'Decrease';

    return '';
  }, [history]);

  const closed = useMemo(
    () =>
      isClosedTrade(history)
        ? (history as
            | UserCloseTradeSuccessful
            | ExecuteCloseSuccessful
            | ExecuteDecreaseOrderSuccessful
            | ClosePosition
            | PositionDecreased)
        : undefined,
    [history],
  );

  const leverage = useMemo(() => {
    return history.getLeverage({
      collateralPrice,
      // FIXME: should use execution fee from history, refine in post mvp
      executionFeeUsd,
      // FIXME: should use openFeeP or openFee from history, refine in post mvp
      openFeeP: pairInfo?.feeConfig?.openFeeP,
      lvTokenDiscountP: pairInfo?.feeConfig?.lvTokenDiscountP,
      pairPrice: history.entryPrice ?? pairPrice,
    });
  }, [
    collateralPrice,
    executionFeeUsd,
    history,
    history.entryPrice,
    pairInfo?.feeConfig?.lvTokenDiscountP,
    pairInfo?.feeConfig?.openFeeP,
    pairPrice,
  ]);

  const pnlPercentage = useMemo(() => {
    if (
      closed?.detail?.executionType === ClosePositionDetailExecutionTypeEnum.Liq
    ) {
      return BigNumber(-1);
    }

    return closed?.detail?.pnlAfterFee.div(history.collateral ?? BigNumber(0));
  }, [closed, history.collateral]);

  const shareBgImage = closed?.detail?.pnlAfterFee?.gte(0)
    ? '/images/share-up.svg'
    : '/images/share-down.svg';

  const initialPerpeIdx = useMemo(() => {
    if (
      closed?.detail?.executionType === ClosePositionDetailExecutionTypeEnum.Liq
    ) {
      return 2;
    }
    const isPositive = closed?.detail?.pnlAfterFee?.gte(0);

    return isPositive ? 0 : 3;
  }, [closed]);

  const [selectedPerpeIdx, setSelectedPerpeIdx] = useState<number | null>(
    initialPerpeIdx,
  );

  const onCopyImage = async () => {
    const node = shareRef.current;
    if (!node || imageBusyRef.current) return;
    imageBusyRef.current = true;
    setImageBusy(true);
    try {
      // Mobile: open the native share sheet (Save to Photos + share anywhere).
      // Clipboard image writes are unreliable on iOS/in-app browsers.
      if (canShare) {
        try {
          await shareNodeImage(node, {
            pixelRatio: 3,
            fileName: 'leverup-share.png',
          });

          return;
        } catch (e) {
          // User dismissed the share sheet — not an error.
          if ((e as { name?: string })?.name === 'AbortError') return;
          // Otherwise fall through to download.
          try {
            await downloadNodeAsPng(node, 'leverup-share.png', {
              pixelRatio: 3,
            });

            return;
          } catch {}
        }
      }

      // Desktop: copy to clipboard.
      try {
        await copyNodeImageToClipboard(node, { pixelRatio: 3 });
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      } catch (e) {
        try {
          basicToaster.error({
            title: 'Copy failed, Please try again',
          });
        } catch {}
      }
    } finally {
      imageBusyRef.current = false;
      setImageBusy(false);
    }
  };

  const onShareX = async () => {
    if (!shareRef.current) return;
    try {
      await copyNodeImageToClipboard(shareRef.current, { pixelRatio: 3 });
    } catch (e) {
      // ignore copy failure; allow user to still tweet without image
    }
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(
      shareText,
    )}`;
    window.open(url, '_blank');
  };

  const onCopyLink = async () => {
    try {
      const origin = window.location.origin;
      const urlToCopy =
        showCode && referralCode
          ? `${origin}?code=${encodeURIComponent(referralCode)}`
          : window.location.href;
      await navigator.clipboard.writeText(urlToCopy);
      basicToaster.success({ title: 'Copied to clipboard' });
    } catch {}
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => setOpen(v.open)}>
      {children ? (
        <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      ) : (
        <Button
          variant="ghost"
          p={0}
          w="28px"
          h="28px"
          minW="28px"
          onClick={() => setOpen(true)}
        >
          <Icon
            boxSize="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <styled.img src="/images/share.svg" width="100%" height="100%" />
          </Icon>
        </Button>
      )}

      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          bg="v2.bg.panel"
          color="greyWhite.lightGrey1"
          display="flex"
          flexDir="column"
          // Cap to the viewport and let the description scroll internally.
          // iOS Safari can't reliably scroll the fixed-position positioner.
          minH={{ base: '0', tablet: 'unset' }}
          maxH={{ base: '100dvh', tablet: '90dvh' }}
          overflow="hidden"
          minW={{
            base: 'full',
            tablet: '500px',
          }}
          maxW={{
            base: 'full',
            tablet: '500px',
          }}
          borderRadius="12px"
        >
          <Dialog.Title asChild>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              px="16px"
              py="12px"
              bg="v2.bg.panel"
              borderRadius="12px"
            >
              <Text textStyle="headings.large">Share Trade</Text>
              <Dialog.CloseTrigger boxShadow="none" bg="none">
                <Icon w="20px" h="20px">
                  <XIcon />
                </Icon>
              </Dialog.CloseTrigger>
            </Flex>
          </Dialog.Title>

          <Dialog.Description
            pt={0}
            display="flex"
            flexDir="column"
            gap={3}
            cursor="default"
            flex="1"
            minH="0"
            overflowY="auto"
            css={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              // Keep children at their natural height so the column overflows
              // and scrolls, instead of flexbox squishing them (which clipped
              // the card and prevented scrolling on iOS).
              '& > *': { flexShrink: 0 },
            }}
          >
            <styled.div
              ref={setScalerWrapRef}
              position="relative"
              className="group"
              w="full"
              overflow="hidden"
              // Hover border via outline (offset inward) so it doesn't eat
              // into the content box and clip the scaled card's bottom edge.
              outline="1px solid"
              outlineColor="transparent"
              outlineOffset="-1px"
              transition="all 0.15s ease"
              cursor="pointer"
              _hover={{
                outlineColor: 'greyWhite.outline',
                filter: 'brightness(1.03)',
              }}
              onClick={onCopyImage}
              style={{ height: SHARE_CARD_HEIGHT * cardScale }}
            >
              <styled.div
                style={{
                  width: `${SHARE_CARD_WIDTH}px`,
                  height: `${SHARE_CARD_HEIGHT}px`,
                  transform: `scale(${cardScale})`,
                  transformOrigin: 'top left',
                }}
              >
                <styled.div
                  ref={shareRef}
                  position="relative"
                  width="500px"
                  height="260px"
                  overflow="hidden"
                  bgGradient="to-br"
                  gradientFrom="#0b0b0b"
                  gradientTo="#1f1f1f"
                >
                  <styled.img
                    src={shareBgImage}
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    pointerEvents="none"
                  />
                  <Flex
                    position="absolute"
                    top="14px"
                    right="16px"
                    alignItems="center"
                    gap={2}
                  >
                    <styled.img
                      src="/images/logo.svg"
                      alt="LeverUp"
                      width="120px"
                      height="32px"
                    />
                  </Flex>
                  <VStack
                    position="absolute"
                    top="16px"
                    left="16px"
                    gap={2}
                    alignItems="flex-start"
                  >
                    <HStack gap={2}>
                      <styled.img
                        src={history.pair.image}
                        width="24px"
                        height="24px"
                      />
                      <Text
                        textStyle="headings.default"
                        color="greyWhite.lightGrey1"
                      >
                        {history.pair.baseName}
                      </Text>
                      <styled.span
                        px={2}
                        py={1}
                        borderRadius="4px"
                        bg={
                          history.isLong
                            ? 'price.priceUpBg'
                            : 'price.priceDownBg'
                        }
                        color={
                          history.isLong ? 'price.priceUp' : 'price.priceDown'
                        }
                        textStyle="text.small"
                      >
                        {history.isLong ? 'Long' : 'Short'}{' '}
                        {leverage ? `${leverage.toFixed(1)}x` : ''}
                      </styled.span>
                      {!!eventLabel && (
                        <styled.span
                          px={2}
                          py={1}
                          borderRadius="4px"
                          bg="greyWhite.darkGary1"
                          color="greyWhite.lightGrey1"
                          textStyle="text.small"
                        >
                          {eventLabel}
                        </styled.span>
                      )}
                    </HStack>
                  </VStack>

                  <VStack
                    position="absolute"
                    left="24px"
                    top="72px"
                    alignItems="flex-start"
                  >
                    <Text
                      textStyle="numbers.medium"
                      fontSize="72px"
                      color="primary.orange"
                      whiteSpace="nowrap"
                    >
                      {pnlPercentage !== undefined && (
                        <Percentage
                          whiteSpace="nowrap"
                          textStyle="numbers.huge"
                          value={pnlPercentage}
                          textDecoration="none"
                          css={{ textDecoration: 'none' }}
                          opts={{
                            isShowSign: true,
                          }}
                          data-cy="PnLToolTip-PnLAfterFeePercentage"
                          data-cy-value={pnlPercentage}
                        />
                      )}
                    </Text>
                  </VStack>

                  <ShareIdentityProfile
                    identity={identity}
                    code={showCode ? referralCode : undefined}
                    position="absolute"
                    left="24px"
                    top="152px"
                  />

                  <VStack
                    position="absolute"
                    left="24px"
                    bottom="18px"
                    gap={1}
                    alignItems="flex-start"
                  >
                    <HStack gap={6}>
                      <VStack
                        alignItems="flex-start"
                        gap={0}
                        whiteSpace="nowrap"
                      >
                        <Text
                          textStyle="text.xsmall"
                          color="greyWhite.lightGrey4"
                        >
                          Close Price
                        </Text>
                        <Text textStyle="numbers.default" whiteSpace="nowrap">
                          {closed?.detail?.closePrice?.toFormat(
                            priceDisplayDecimals,
                          ) ?? '--'}
                        </Text>
                      </VStack>
                    </HStack>
                  </VStack>
                  {selectedPerpeIdx !== null && (
                    <styled.img
                      src={PEPE_IMAGES[selectedPerpeIdx]}
                      position="absolute"
                      right="24px"
                      bottom="0px"
                      width="180px"
                      height="180px"
                    />
                  )}

                  <styled.div
                    data-ignore-capture="true"
                    position="absolute"
                    top="12px"
                    right="12px"
                    display={copied ? 'block' : 'none'}
                    bg="#19191e"
                    color="greyWhite.lightGrey1"
                    border="1px solid"
                    borderColor="greyWhite.outline"
                    px="10px"
                    py="6px"
                    borderRadius="8px"
                    textStyle="text.small"
                    pointerEvents="none"
                  >
                    Copied to clipboard
                  </styled.div>
                </styled.div>
              </styled.div>

              {/* Hover Overlay */}
              <Flex
                position="absolute"
                top={0}
                left={0}
                w="full"
                h="full"
                alignItems="center"
                justifyContent="center"
                bg="rgba(0,0,0,0.3)"
                opacity={0}
                transition="opacity 0.2s"
                _groupHover={{
                  opacity: {
                    base: 0,
                    md: 1,
                  },
                }}
                pointerEvents="none"
              >
                <HStack
                  bg="rgba(0,0,0,0.7)"
                  px={3}
                  py={2}
                  borderRadius="8px"
                  gap={2}
                  backdropFilter="blur(4px)"
                >
                  <Icon boxSize="16px" color="white">
                    <Copy />
                  </Icon>
                  <Text
                    textStyle="text.small"
                    color="white"
                    fontWeight="medium"
                  >
                    Click to Copy
                  </Text>
                </HStack>
              </Flex>
            </styled.div>

            <ShareIdentityControls identity={identity} />

            <VStack px={6} alignItems="flex-start" gap={2}>
              <Text textStyle="headings.small" color="greyWhite.lightGrey3">
                Customize your text
              </Text>
              <Textarea
                value={shareText}
                color="greyWhite.lightGrey3"
                borderRadius="8px"
                border="1px solid"
                borderColor="greyWhite.outline"
                bg="#19191e"
                onChange={(e) => setShareText(e.target.value)}
                placeholder="Add a caption"
                rows={2}
                outline="none"
                boxShadow="none"
                _focusVisible={{
                  borderColor: 'greyWhite.outline',
                  boxShadow: 'none',
                  outline: 'none',
                }}
              />
            </VStack>

            <VStack px={6} alignItems="flex-start" gap={2}>
              <Text textStyle="headings.small" color="greyWhite.lightGrey3">
                Add an UltraPepe
              </Text>
              <HStack gap={2} flexWrap="wrap" width="100%">
                <styled.button
                  onClick={() => setSelectedPerpeIdx(null)}
                  width="18%"
                  height="60px"
                  borderRadius="8px"
                  bg="#19191e"
                  border="2px solid"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  overflow="hidden"
                  borderColor={
                    selectedPerpeIdx === null ? 'primary.orange' : 'transparent'
                  }
                >
                  <styled.img
                    src="/images/empty.svg"
                    width="30%"
                    height="30%"
                  />
                </styled.button>
                {PEPE_IMAGES.map((src, idx) => (
                  <styled.button
                    key={src}
                    onClick={() => setSelectedPerpeIdx(idx)}
                    p={2}
                    bg="#19191e"
                    width="18%"
                    height="60px"
                    borderRadius="8px"
                    cursor="pointer"
                    overflow="hidden"
                    border="2px solid"
                    borderColor={
                      selectedPerpeIdx === idx
                        ? 'primary.orange'
                        : 'transparent'
                    }
                  >
                    <styled.img src={src} width="100%" height="100%" />
                  </styled.button>
                ))}
              </HStack>
            </VStack>

            <ShareInviteCodeToggle
              px={6}
              checked={showCode}
              onCheckedChange={setShowCode}
              hasCode={!!referralCode}
              isLoading={isReferralCodeLoading}
            />

            <VStack px={6} alignItems="flex-start" gap={1}>
              <Text textStyle="text.small" color="greyWhite.lightGrey3">
                {canShare
                  ? "Tip: Tap 'Save Image' to save the card, then attach it to your post."
                  : "Tip: 'Share on X' automatically copies the image. Just paste (Ctrl+V) in your tweet."}
              </Text>
            </VStack>

            <Stack
              p={5}
              pt={0}
              gap={3}
              direction={{ base: 'column', md: 'row' }}
            >
              <Button onClick={onShareX} variant="primary" size="big" w="full">
                <HStack gap={2}>
                  <styled.img
                    src="/images/x-black.svg"
                    width="18px"
                    height="18px"
                  />
                  <span>Share on X</span>
                </HStack>
              </Button>

              <Button
                onClick={onCopyImage}
                loading={imageBusy}
                disabled={imageBusy}
                variant="secondary"
                size="big"
                w="full"
                border="1px solid #CCFF0066"
                color="#FFD600"
                _hover={{
                  borderColor: '#FFD600',
                  color: '#FFD600',
                  bg: 'rgba(255,214,0,0.1)',
                }}
              >
                <HStack gap={2}>
                  <Icon boxSize="5" color="#FFD600">
                    <Copy />
                  </Icon>
                  <span>{canShare ? 'Save Image' : 'Copy Image'}</span>
                </HStack>
              </Button>

              <Button
                onClick={onCopyLink}
                variant="secondary"
                size="big"
                w="full"
                border="1px solid #CCFF0066"
                color="#FFD600"
                _hover={{
                  borderColor: '#FFD600',
                  color: '#FFD600',
                  bg: 'rgba(255,214,0,0.1)',
                }}
              >
                <HStack gap={2}>
                  <Icon boxSize="5" color="#FFD600">
                    <ShareLink />
                  </Icon>
                  <span>Copy Link</span>
                </HStack>
              </Button>
            </Stack>
          </Dialog.Description>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
